import { MetadataType } from '@aibees/elephant/hooks';
import { CompanySocialMedia, SearchType } from '@aibees/elephant/types';
import { companySocialMediaFilterLabelParser } from '../components/filter-tags/utils';
import {
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';

export default function socialMediaFilterConfig(
  searchType: SearchType
): SearchFilterBase {
  const isCompanySearch = searchType === SearchType.Company;
  const companiesSocialMedia: SearchFilter = {
    type: SearchFilterType.NONE,
    index: 3,
    field: 'social_media_company',
    key: 'social_media_company',
    name: 'Company',
    metadata: {
      tabs: {
        icon: 'business',
        iconColor: 'primary',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Company Social Media',
        },
      },
    },
    nested: true,
    mainItem: true,
    open: true,
    sections: { main: 2, more: 1 },
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        icon: undefined,
        index: 0,
        key: 'social_media_company_include_social_media',
        field: 'social_media_company_include_social_media',
        nested: true,
        open: true,
        layer: 4,
        metadata: {
          color: 'primary',
          placeholder: 'Select Social Media',
          autocomplete: {
            recent: false,
            checkboxPosition: 'left',
            multiple: true,
            customOptions: true,
          },
          metaDataApi: {
            enabled: true,
            key: MetadataType.COMPANY_SOCIAL_MEDIA,
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        utils: {
          tags: { labelFormatter: companySocialMediaFilterLabelParser },
        },
        treeOptions: COMPANY_SOCIAL_MEDIA_FILTER_OPTIONS,
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        icon: undefined,
        index: 0,
        key: 'social_media_company_exclude_social_media',
        field: 'social_media_company_exclude_social_media',
        nested: true,
        open: true,
        layer: 4,
        metadata: {
          color: 'error',
          icon: 'block',
          placeholder: 'Select Social Media',
          autocomplete: {
            recent: false,
            checkboxPosition: 'left',
            multiple: true,
            customOptions: true,
          },
          metaDataApi: {
            enabled: true,
            key: MetadataType.COMPANY_SOCIAL_MEDIA,
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        utils: {
          tags: { labelFormatter: companySocialMediaFilterLabelParser },
        },
        treeOptions: COMPANY_SOCIAL_MEDIA_FILTER_OPTIONS,
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        icon: undefined,
        index: 0,
        key: 'social_media_company_all_of_social_media',
        field: 'social_media_company_all_of_social_media',
        nested: true,
        open: true,
        layer: 4,
        metadata: {
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Select Social Media',
          autocomplete: {
            recent: false,
            checkboxPosition: 'left',
            multiple: true,
            customOptions: true,
          },
          metaDataApi: {
            enabled: true,
            key: MetadataType.COMPANY_SOCIAL_MEDIA,
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        utils: {
          tags: { labelFormatter: companySocialMediaFilterLabelParser },
        },
        treeOptions: COMPANY_SOCIAL_MEDIA_FILTER_OPTIONS,
      },
    ],
  };

  const peopleSocialMedia: SearchFilter = {
    type: SearchFilterType.NONE,
    index: 2,
    field: 'social_media_contacts',
    key: 'social_media_contacts',
    name: 'People',
    metadata: {
      tabs: {
        icon: 'people',
        iconColor: 'success',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Contacts Social Media',
        },
      },
    },
    nested: true,
    mainItem: true,
    comingSoon: true,
    disabled: true,
    sections: { main: 2, more: 1 },
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        icon: undefined,
        index: 0,
        key: 'social_media_contacts_include_social_media',
        field: 'social_media_contacts_include_social_media',
        nested: true,
        open: true,
        layer: 4,
        metadata: {
          color: 'primary',
          placeholder: 'Select Social Media',
          autocomplete: {
            recent: false,
            checkboxPosition: 'left',
            multiple: true,
            customOptions: true,
          },
          metaDataApi: {
            enabled: true,
            key: MetadataType.PERSON_SOCIAL_MEDIA,
          },
          checkbox: {
            comingSoonKeys: ['TWITTER', 'FACEBOOK', 'GITHUB'],
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        utils: {
          tags: { labelFormatter: companySocialMediaFilterLabelParser },
        },
        treeOptions: COMPANY_SOCIAL_MEDIA_FILTER_OPTIONS,
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        icon: undefined,
        index: 0,
        key: 'social_media_contacts_exclude_social_media',
        field: 'social_media_contacts_exclude_social_media',
        nested: true,
        open: true,
        layer: 4,
        metadata: {
          color: 'error',
          icon: 'block',
          placeholder: 'Select Social Media',
          autocomplete: {
            recent: false,
            checkboxPosition: 'left',
            multiple: true,
            customOptions: true,
          },
          metaDataApi: {
            enabled: true,
            key: MetadataType.PERSON_SOCIAL_MEDIA,
          },
          checkbox: {
            comingSoonKeys: ['TWITTER', 'FACEBOOK', 'GITHUB'],
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        utils: {
          tags: { labelFormatter: companySocialMediaFilterLabelParser },
        },
        treeOptions: COMPANY_SOCIAL_MEDIA_FILTER_OPTIONS,
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        icon: undefined,
        index: 0,
        key: 'social_media_contacts_all_of_social_media',
        field: 'social_media_contacts_all_of_social_media',
        nested: true,
        open: true,
        layer: 4,
        metadata: {
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Select Social Media',
          autocomplete: {
            recent: false,
            checkboxPosition: 'left',
            multiple: true,
            customOptions: true,
          },
          metaDataApi: {
            enabled: true,
            key: MetadataType.PERSON_SOCIAL_MEDIA,
          },
          checkbox: {
            comingSoonKeys: ['TWITTER', 'FACEBOOK', 'GITHUB'],
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        utils: {
          tags: { labelFormatter: companySocialMediaFilterLabelParser },
        },
        treeOptions: COMPANY_SOCIAL_MEDIA_FILTER_OPTIONS,
      },
    ],
  };

  if (isCompanySearch) {
    return {
      ...companiesSocialMedia,
      name: 'Social Media',
      icon: 'hub',
      index: 1,
      type: SearchFilterType.ROOT,
      category: [FilterCategory.COMPANY_DATA],
      pinned: true,
    };
  }

  return {
    name: 'Social Media',
    icon: 'hub',
    index: 1,
    type: SearchFilterType.ROOT,
    category: isCompanySearch
      ? [FilterCategory.COMPANY_DATA]
      : [FilterCategory.COMPANY_DATA, FilterCategory.CONTACT_DATA],
    pinned: true,
    items: isCompanySearch
      ? [companiesSocialMedia]
      : [
          {
            name: '',
            type: SearchFilterType.BUTTON_GROUP,
            icon: undefined,
            index: 0,
            key: '',
            field: '',
            metadata: {
              allowParallelItems: true,
              tabs: {
                defaultOpenIndex: 0,
              },
            },
            config: { labelKey: 'label', valueKey: 'value' },
            items: [
              {
                name: 'Applied Filters',
                type: SearchFilterType.APPLIED_FILTERS,
                index: 2,
                icon: undefined,
                key: 'social_media_applied_filters',
                field: 'social_media_applied_filters',
                metadata: {},
              },
              companiesSocialMedia,
              peopleSocialMedia,
            ],
          },
        ],
  };
}

const getCompanySocialMediaOptions = () =>
  Object.values(CompanySocialMedia).map((companySocialMedia) => ({
    id: companySocialMedia,
    value: companySocialMedia,
    label: companySocialMediaFilterLabelParser(
      companySocialMedia,
      companySocialMedia
    ),
  }));

export const COMPANY_SOCIAL_MEDIA_FILTER_OPTIONS = [
  getCompanySocialMediaOptions(),
];
